import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Checkbox,
  colors,
  IconButton,
  InputLabel,
  Select,
  Stack,
  TextareaAutosize,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, Download, Undo } from '@mui/icons-material';

import CountryPicker from '../../../common/CountryPicker';
import CountryAutocomplete from '../../../common/CountryAutocomplete';
import FileUpload from '../../../common/inputs/FileUpload';
import HelpTooltip from '../../../common/HelpTooltip';
import YesNoSelect from '../../../common/inputs/YesNoSelect';

export const CountryInput = ({
  id,
  name,
  label,
  value,
  required = false,
  onChange,
  tooltip,
  highlighted = false,
}) => {
  const handleChange = (_event, newValue) => {
    onChange(name, newValue.country_code_id, newValue);
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
    >
      <InputLabel
        sx={{
          width: { md: 350 },
          color: highlighted && required && !value ? colors.orange[700] : '',
        }}
        required={required}
      >
        {label}
        {tooltip && <HelpTooltip message={tooltip} />}
      </InputLabel>

      <CountryPicker
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        size="small"
        selectDefault={true}
        label=""
      />
    </Stack>
  );
};

export const CountryAutocompleteInput = ({
  id,
  name,
  label,
  value,
  required = false,
  onChange,
  tooltip,
  highlighted = false,
}) => {
  const handleChange = (_event, newValue) => {
    onChange(name, newValue);
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
    >
      <InputLabel
        sx={{
          width: { md: 350 },
          color: highlighted && required && !value ? colors.orange[700] : '',
        }}
        required={required}
      >
        {label}
        {tooltip && <HelpTooltip message={tooltip} />}
      </InputLabel>

      <CountryAutocomplete
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        size="small"
        label=""
        fullWidth
      />
    </Stack>
  );
};

export const TextInput = ({
  id,
  name,
  label,
  value,
  required = false,
  onChange,
  tooltip,
  error = false,
  disabled = false,
  highlighted = false,
  multiline = false,
  rows = 1,
}) => {
  const handleChange = (event) => {
    onChange(name, event.target.value);
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'flex-start' }}
    >
      <InputLabel
        sx={{
          width: { md: 350 },
          color: highlighted && required && !value ? colors.orange[700] : '',
        }}
        required={required}
      >
        {label}
        {tooltip && <HelpTooltip message={tooltip} />}
      </InputLabel>

      <TextField
        fullWidth
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        size="small"
        error={error}
        helperText={error || ''}
        maxLength={800}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        sx={{ '& .MuiInputBase-inputMultiline': { resize: 'vertical' } }}
      />
    </Stack>
  );
};

export const FileInput = ({
  id,
  name,
  label,
  value,
  tooltip,
  required = false,
  multiple = false,
  onChange,
  onDownload,
  highlighted = false,
}) => {
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!value) return;

    console.log('value', value);

    if (Array.isArray(value)) {
      setFiles(value);
      return;
    }

    setFiles([value]);
  }, [value]);

  const handleChange = (event) => {
    const newFiles = [...files];

    Object.values(event.target.files).forEach((f) => {
      if (typeof f === 'number') return;

      newFiles.push({ attachment_name: f.name, attachment_file: f });
    });

    setFiles(newFiles);
    onChange(name, newFiles);
  };

  const handleDelete = (index) => {
    const newFiles = files
      .map((file, i) => {
        if (i !== index) return file;
        if (!file.id) return;

        return { ...file, deleted: true };
      })
      .filter((f) => !!f);

    setFiles(newFiles);
    onChange(name, newFiles);
  };

  const handleRestore = (index) => {
    const newFiles = files
      .map((file, i) => {
        if (i === index) return { ...file, deleted: false };
        if (multiple) return file;
        if (file.id) return { ...file, deleted: true };
        return undefined;
      })
      .filter((f) => !!f);

    setFiles(newFiles);
    onChange(name, newFiles);
  };

  const buttonDisabled = () => {
    if (multiple) return false;

    if (files.length === 0) return false;

    return files.some((f) => !f.deleted);
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'flex-start' }}
    >
      <InputLabel
        sx={{
          width: { md: 350 },
          color:
            highlighted && required && !value?.length ? colors.orange[700] : '',
        }}
        required={required}
      >
        {label}
        {tooltip && <HelpTooltip message={tooltip} />}
      </InputLabel>
      <Stack direction="column">
        {files?.map((f, index) => (
          <Stack key={index} direction="row" alignItems="center">
            <Tooltip title={f.attachment_name}>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  textDecoration: f.deleted ? 'line-through' : '',
                  maxWidth: '200px',
                }}
              >
                {f.attachment_name}
              </Typography>
            </Tooltip>
            {f.customer_attachment_id && (
              <IconButton size="small" onClick={() => onDownload(f)}>
                <Download color="secondary" fontSize="small" />
              </IconButton>
            )}
            {f.deleted && (
              <IconButton size="small" onClick={() => handleRestore(index)}>
                <Undo color="secondary" fontSize="small" />
              </IconButton>
            )}
            {!f.deleted && (
              <IconButton size="small" onClick={() => handleDelete(index)}>
                <Delete color="error" fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}

        <FileUpload
          id={id}
          name={name}
          onChange={handleChange}
          multiple={multiple}
          disabled={buttonDisabled()}
        />
      </Stack>
    </Stack>
  );
};

export const SelectInput = ({
  id,
  name,
  label,
  value,
  required = false,
  onChange,
  children,
  tooltip,
  highlighted = false,
}) => {
  const handleChange = (event) => {
    onChange(name, event.target.value);
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
    >
      <InputLabel
        sx={{
          width: { md: 350 },
          color: highlighted && required && !value ? colors.orange[700] : '',
        }}
        required={required}
      >
        {label}
        {tooltip && <HelpTooltip message={tooltip} />}
      </InputLabel>

      <Select
        id={id}
        name={name}
        value={value || ''}
        onChange={handleChange}
        size="small"
        fullWidth
      >
        {children}
      </Select>
    </Stack>
  );
};

export const AutocompleteInput = ({
  id,
  name,
  label,
  value,
  required = false,
  onChange,
  options,
  tooltip,
  highlighted = false,
}) => {
  const handleChange = (_event, newValue) => {
    onChange(name, newValue);
  };

  let inputValue;
  switch (typeof value) {
    case 'string':
      inputValue = {
        id: value,
        label: options.find((o) => o.id === value).label,
      };
      break;
    case 'object':
      inputValue = value;
      break;
    default:
      inputValue = null;
  }

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
    >
      <InputLabel
        sx={{
          width: { md: 350 },
          color: highlighted && required && !value ? colors.orange[700] : '',
        }}
        required={required}
      >
        {label}
        {tooltip && <HelpTooltip message={tooltip} />}
      </InputLabel>

      <Autocomplete
        id={id}
        name={name}
        value={inputValue}
        options={options}
        onChange={handleChange}
        disablePortal
        fullWidth
        isOptionEqualToValue={(option, value) => {
          if (typeof value === 'string') return option.id === value;
          return option.id === value.id;
        }}
        renderInput={(params) => (
          <TextField {...params} fullWidth size="small" />
        )}
      />
    </Stack>
  );
};

export const YesNoInput = ({
  id,
  name,
  label,
  value,
  required = false,
  onChange,
  tooltip,
  helperText,
  highlighted = false,
}) => {
  const handleChange = (_event, newValue) => {
    onChange(name, newValue);
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
    >
      <InputLabel
        sx={{
          width: { md: 350 },
          color: highlighted && required && !value ? colors.orange[700] : '',
        }}
        required={required}
      >
        {label}
        {tooltip && <HelpTooltip message={tooltip} />}
      </InputLabel>

      <YesNoSelect
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        size="small"
        helperText={helperText}
        useBoolean
      />
    </Stack>
  );
};

export const CheckboxInput = ({
  id,
  name,
  label,
  checked,
  required = false,
  onChange,
  tooltip,
  highlighted = false,
}) => {
  const handleChange = (event) => {
    onChange(name, event.target.checked);
  };

  console.log('highlighted', name, checked, required, highlighted);

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
    >
      <InputLabel
        sx={{
          width: { md: 350 },
          color: highlighted && required && !checked ? colors.orange[700] : '',
        }}
        required={required}
      >
        {label}
        {tooltip && <HelpTooltip message={tooltip} />}
      </InputLabel>
      <Checkbox
        id={id}
        name={name}
        onChange={handleChange}
        checked={checked}
        value="1"
      />
    </Stack>
  );
};
